/**
 * Lift2Fit — admin: Plans & pricing (`#/admin/plans`).
 *
 * The pricing models are DATA here, not constants: one catalog row per sellable
 * plan for both products, with its price, currency, seat limit and active/
 * retired state. The admin edits a row and every change is audited.
 *
 * IMPORTANT: this screen manages the catalog only. The marketing pages still
 * render their own copy (their own price strings), so an edit here is the
 * source of truth the interfaces will consume later, not a live price change on
 * the lifter or CRM pages.
 */
import { useEffect, useState } from 'react';
import {
  api,
  type AdminAuditEntry,
  type PlanAudience,
  type PlanCatalogEntry,
} from '@/lib/api';
import { formatDate } from '@/lib/format';
import { AlertIcon, MonitorIcon, TargetIcon, TrendUpIcon } from '@/lib/icons';
import {
  AppLinkButton,
  EmptyState,
  LoadingPanel,
  LiveMessage,
  Panel,
  ScreenHeader,
  StatusChip,
} from '../components';
import { formatMoney } from '../crm/shared';
import { formatCompactCount, formatCount } from '@/lib/format';
import { useAsync } from '../hooks';
import { ADMIN_EYEBROW, AdminStat, seatLimitLabel } from './shared';

const AUDIENCE_LABEL: Record<PlanAudience, string> = {
  lifter: 'Lifter plans',
  coach: 'Coaching plans',
};

interface DraftPlan {
  displayName: string;
  priceMonthly: string;
  currency: string;
  seatLimit: string;
  negotiated: boolean;
  active: boolean;
  sortOrder: string;
  accountValue: string;
}

function draftOf(plan: PlanCatalogEntry): DraftPlan {
  return {
    displayName: plan.displayName,
    priceMonthly: String(plan.priceMonthly),
    currency: plan.currency,
    seatLimit: plan.seatLimit === null ? '' : String(plan.seatLimit),
    negotiated: plan.negotiated,
    active: plan.active,
    sortOrder: String(plan.sortOrder),
    accountValue: plan.accountValue,
  };
}

function PlanRowEditor({
  plan,
  onSaved,
  onAudit,
}: {
  plan: PlanCatalogEntry;
  onSaved: (plan: PlanCatalogEntry) => void;
  onAudit: (audit: AdminAuditEntry | null) => void;
}) {
  const [draft, setDraft] = useState<DraftPlan>(() => draftOf(plan));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(draftOf(plan));
  }, [plan]);

  const save = async () => {
    setBusy(true);
    setError(null);
    const price = Number(draft.priceMonthly);
    const seats = draft.seatLimit.trim() === '' ? null : Number(draft.seatLimit);
    const order = Number(draft.sortOrder);
    if (!Number.isFinite(price) || price < 0) {
      setError('Price must be a positive number.');
      setBusy(false);
      return;
    }
    if (seats !== null && (!Number.isInteger(seats) || seats < 1)) {
      setError('Seat limit must be a whole number of at least 1, or blank for unlimited.');
      setBusy(false);
      return;
    }
    const result = await api.adminUpdatePlan(plan.planId, {
      displayName: draft.displayName.trim(),
      priceMonthly: price,
      currency: draft.currency.trim().toUpperCase(),
      seatLimit: seats,
      negotiated: draft.negotiated,
      active: draft.active,
      sortOrder: Number.isFinite(order) ? order : 0,
      accountValue: draft.accountValue.trim(),
    });
    setBusy(false);
    if (result.problem || !result.data) {
      setError(result.problem?.detail ?? result.problem?.title ?? 'The plan did not save.');
      return;
    }
    onSaved(result.data.plan);
    onAudit(result.data.audit);
  };

  return (
    <tr data-testid={`admin-plan-row-${plan.planId}`} className="border-b border-hairline last:border-b-0 align-top">
      <td className="py-3 pr-3">
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[13px] font-bold text-ink">{plan.planKey}</span>
          <span className="text-[11px] text-ink-muted">
            stored as <span className="font-mono">{plan.accountValue}</span> · updated {formatDate(plan.updatedAt)}
          </span>
        </span>
      </td>
      <td className="py-3 pr-3">
        <input
          data-testid={`admin-plan-name-${plan.planId}`}
          value={draft.displayName}
          onChange={(event) => setDraft({ ...draft, displayName: event.target.value })}
          className="h-9 w-32 border border-hairline bg-page px-2 text-[13px] text-ink"
          aria-label={`${plan.planKey} display name`}
        />
      </td>
      <td className="py-3 pr-3">
        <span className="flex items-center gap-1">
          <input
            data-testid={`admin-plan-price-${plan.planId}`}
            value={draft.priceMonthly}
            inputMode="decimal"
            onChange={(event) => setDraft({ ...draft, priceMonthly: event.target.value })}
            className="l2f-tabular h-9 w-20 border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
            aria-label={`${plan.planKey} monthly price`}
          />
          <input
            data-testid={`admin-plan-currency-${plan.planId}`}
            value={draft.currency}
            onChange={(event) => setDraft({ ...draft, currency: event.target.value })}
            className="h-9 w-16 border border-hairline bg-page px-2 font-mono text-[12px] uppercase text-ink"
            aria-label={`${plan.planKey} currency`}
          />
        </span>
      </td>
      <td className="py-3 pr-3">
        <input
          data-testid={`admin-plan-seats-${plan.planId}`}
          value={draft.seatLimit}
          inputMode="numeric"
          placeholder="unlimited"
          onChange={(event) => setDraft({ ...draft, seatLimit: event.target.value })}
          className="l2f-tabular h-9 w-24 border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
          aria-label={`${plan.planKey} seat limit`}
        />
      </td>
      <td className="py-3 pr-3">
        <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.08em] text-ink-muted">
          <input
            type="checkbox"
            data-testid={`admin-plan-negotiated-${plan.planId}`}
            checked={draft.negotiated}
            onChange={(event) => setDraft({ ...draft, negotiated: event.target.checked })}
          />
          Negotiated
        </label>
      </td>
      <td className="py-3 pr-3">
        <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.08em] text-ink-muted">
          <input
            type="checkbox"
            data-testid={`admin-plan-active-${plan.planId}`}
            checked={draft.active}
            onChange={(event) => setDraft({ ...draft, active: event.target.checked })}
          />
          {draft.active ? 'Active' : 'Retired'}
        </label>
      </td>
      <td className="py-3">
        <span className="flex flex-col gap-1">
          <button
            type="button"
            data-testid={`admin-plan-save-${plan.planId}`}
            disabled={busy}
            onClick={() => void save()}
            className="inline-flex h-9 w-fit items-center border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
          {error ? (
            <span role="alert" className="max-w-[24ch] text-[11px] text-danger">
              {error}
            </span>
          ) : null}
        </span>
      </td>
    </tr>
  );
}

function NewPlanForm({ onCreated, onAudit }: { onCreated: (plan: PlanCatalogEntry) => void; onAudit: (audit: AdminAuditEntry | null) => void }) {
  const [audience, setAudience] = useState<PlanAudience>('coach');
  const [planKey, setPlanKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [priceMonthly, setPriceMonthly] = useState('0');
  const [currency, setCurrency] = useState('GBP');
  const [seatLimit, setSeatLimit] = useState('');
  const [accountValue, setAccountValue] = useState('coach');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    setBusy(true);
    setError(null);
    const price = Number(priceMonthly);
    const seats = seatLimit.trim() === '' ? null : Number(seatLimit);
    if (planKey.trim() === '' || displayName.trim() === '' || accountValue.trim() === '') {
      setError('Key, name and stored value are all required.');
      setBusy(false);
      return;
    }
    const result = await api.adminCreatePlan({
      audience,
      planKey: planKey.trim().toLowerCase(),
      displayName: displayName.trim(),
      priceMonthly: Number.isFinite(price) ? price : 0,
      currency: currency.trim().toUpperCase(),
      seatLimit: seats === null || !Number.isInteger(seats) || seats < 1 ? null : seats,
      negotiated: false,
      active: true,
      sortOrder: 50,
      accountValue: accountValue.trim(),
    });
    setBusy(false);
    if (result.problem || !result.data) {
      setError(result.problem?.detail ?? result.problem?.title ?? 'The plan was not created.');
      return;
    }
    onCreated(result.data.plan);
    onAudit(result.data.audit);
    setPlanKey('');
    setDisplayName('');
  };

  return (
    <div className="flex flex-col gap-3 border border-dashed border-hairline bg-page-subtle p-4">
      <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink">Add a plan</p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Audience</span>
          <select
            data-testid="admin-plan-new-audience"
            value={audience}
            onChange={(event) => setAudience(event.target.value as PlanAudience)}
            className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
          >
            <option value="lifter">Lifter</option>
            <option value="coach">Coaching</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Key</span>
          <input
            data-testid="admin-plan-new-key"
            value={planKey}
            placeholder="elite"
            onChange={(event) => setPlanKey(event.target.value)}
            className="h-9 w-28 border border-hairline bg-page px-2 text-[13px] text-ink"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Name</span>
          <input
            data-testid="admin-plan-new-name"
            value={displayName}
            placeholder="Elite"
            onChange={(event) => setDisplayName(event.target.value)}
            className="h-9 w-32 border border-hairline bg-page px-2 text-[13px] text-ink"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Price / month</span>
          <input
            data-testid="admin-plan-new-price"
            value={priceMonthly}
            inputMode="decimal"
            onChange={(event) => setPriceMonthly(event.target.value)}
            className="l2f-tabular h-9 w-20 border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Seats</span>
          <input
            data-testid="admin-plan-new-seats"
            value={seatLimit}
            placeholder="unlimited"
            onChange={(event) => setSeatLimit(event.target.value)}
            className="l2f-tabular h-9 w-24 border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Stored value</span>
          <input
            data-testid="admin-plan-new-value"
            value={accountValue}
            onChange={(event) => setAccountValue(event.target.value)}
            className="h-9 w-24 border border-hairline bg-page px-2 font-mono text-[12px] text-ink"
          />
        </label>
        <button
          type="button"
          data-testid="admin-plan-new-create"
          disabled={busy}
          onClick={() => void create()}
          className="inline-flex h-9 items-center bg-accent px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-40"
        >
          {busy ? 'Creating…' : 'Add plan'}
        </button>
      </div>
      {error ? (
        <p role="alert" data-testid="admin-plan-new-error" className="text-[12px] text-danger">
          {error}
        </p>
      ) : null}
      <p className="text-[11px] text-ink-muted">
        The stored value is what the product writes to the account: a lifter tier (<span className="font-mono">basic</span>,{' '}
        <span className="font-mono">premium</span>) or a coaching plan (<span className="font-mono">free</span>,{' '}
        <span className="font-mono">coach</span>, <span className="font-mono">gym</span>,{' '}
        <span className="font-mono">gym_plus</span>, <span className="font-mono">scale</span>).
      </p>
    </div>
  );
}

export function AdminPlansScreen() {
  const state = useAsync(() => api.adminPlans(), []);
  const [plans, setPlans] = useState<PlanCatalogEntry[]>([]);
  const [audit, setAudit] = useState<AdminAuditEntry | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (state.data) setPlans(state.data.data);
  }, [state.data]);

  const applySaved = (plan: PlanCatalogEntry) => {
    setPlans((current) => current.map((entry) => (entry.planId === plan.planId ? plan : entry)));
    setNotice(`Saved ${plan.audience} plan ${plan.planKey}.`);
  };
  const applyCreated = (plan: PlanCatalogEntry) => {
    setPlans((current) => [...current, plan]);
    setNotice(`Created ${plan.audience} plan ${plan.planKey}.`);
  };

  if (state.loading && plans.length === 0) {
    return <LoadingPanel label="Loading the plan catalog" rows={5} />;
  }

  if (plans.length === 0) {
    return (
      <EmptyState
        title="The plan catalog did not load"
        body={state.error ?? 'The admin API did not answer. Sign in as an administrator and try again.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/admin" variant="outline">
            Back to the overview
          </AppLinkButton>
        }
      />
    );
  }

  const lifterPlans = plans.filter((plan) => plan.audience === 'lifter');
  const coachPlans = plans.filter((plan) => plan.audience === 'coach');
  const paid = plans.filter((plan) => plan.active && plan.priceMonthly > 0 && !plan.negotiated);

  return (
    <div className="flex flex-col gap-6" data-testid="admin-plans">
      <ScreenHeader
        eyebrow={ADMIN_EYEBROW}
        title="Plans & pricing"
        badge={<StatusChip tone="danger">Founder only</StatusChip>}
        lead="The pricing models as data: list price, currency, seat limit and active state for both products. Every edit is audited. The customer pages still render their own copy, so this is the source the admin manages now and the interfaces can consume later."
        actions={
          <AppLinkButton to="/admin/revenue" variant="outline">
            <TrendUpIcon className="text-[14px]" />
            Revenue
          </AppLinkButton>
        }
      />

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="admin-plans-totals">
        <AdminStat label="Catalog plans" value={formatCompactCount(plans.length)} valueTitle={formatCount(plans.length)} hint="active and retired" />
        <AdminStat label="Active paid plans" value={formatCompactCount(paid.length)} valueTitle={formatCount(paid.length)} hint="excluded: free & negotiated" />
        <AdminStat label="Lifter plans" value={formatCompactCount(lifterPlans.length)} valueTitle={formatCount(lifterPlans.length)} />
        <AdminStat label="Coaching plans" value={formatCompactCount(coachPlans.length)} valueTitle={formatCount(coachPlans.length)} />
      </section>

      {notice ? <LiveMessage message={notice} tone="success" /> : null}

      {(['lifter', 'coach'] as PlanAudience[]).map((audience) => (
        <Panel
          key={audience}
          title={AUDIENCE_LABEL[audience]}
          subtitle="Edit price, currency, seats and active state in place; Save writes the change and its audit row together."
          testId={`admin-plans-${audience}`}
          actions={
            audience === 'lifter' ? (
              <TargetIcon className="text-[14px] text-ink-muted" />
            ) : (
              <MonitorIcon className="text-[14px] text-ink-muted" />
            )
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[940px] border-collapse text-left">
              <thead>
                <tr className="border-b border-hairline text-[10px] uppercase tracking-[0.1em] text-ink-muted">
                  <th className="py-2 pr-3 font-bold">Key</th>
                  <th className="py-2 pr-3 font-bold">Name</th>
                  <th className="py-2 pr-3 font-bold">Price</th>
                  <th className="py-2 pr-3 font-bold">Seats</th>
                  <th className="py-2 pr-3 font-bold">Negotiated</th>
                  <th className="py-2 pr-3 font-bold">State</th>
                  <th className="py-2 font-bold">Write</th>
                </tr>
              </thead>
              <tbody>
                {plans
                  .filter((plan) => plan.audience === audience)
                  .map((plan) => (
                    <PlanRowEditor
                      key={plan.planId}
                      plan={plan}
                      onSaved={applySaved}
                      onAudit={setAudit}
                    />
                  ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-ink-muted">
            Current list price:{' '}
            {plans
              .filter((plan) => plan.audience === audience && plan.active)
              .map((plan) => `${plan.displayName} ${plan.negotiated ? 'talk to us' : formatMoney(plan.priceMonthly, plan.currency)} · ${seatLimitLabel(plan.seatLimit)} seats`)
              .join('  ·  ')}
          </p>
        </Panel>
      ))}

      <NewPlanForm onCreated={applyCreated} onAudit={setAudit} />

      {audit ? (
        <p
          className="border-l-2 border-accent bg-page-subtle px-3 py-2 text-[12px] text-ink-muted"
          data-testid="admin-plans-last-audit"
        >
          Audited: <span className="font-bold text-ink">{audit.summary}</span> · {audit.actorEmail ?? 'admin'} ·{' '}
          {formatDate(audit.createdAt)}
        </p>
      ) : null}
    </div>
  );
}
